import { HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { withStatusMessage } from '@app/core/services/status-message-interceptor';
import { ApiStatusTransitions } from '@app/shared/models/api-status-transitions';
import { StatusTransition } from '@app/shared/models/status-transition';
import { Company } from '../models/company';
import { ApiBase } from '../models/api-base';
import { ApiGet } from '../models/api-get';
import { ApiGetById } from '../models/api-get-by-id';
import { ApiSetStatus } from '../models/api-set-status';
import { ApiUpdate } from '../models/api-update';
import { ApiDelete } from '../models/api-delete';
import { ApiGetExport } from '../models/api-get-export';
import { ApiGetCompanyOwnExport } from '../models/api-get-company-own-export';

@Injectable({
  providedIn: 'root',
})
export class CompanyApi
  extends ApiBase
  implements
    ApiGet<Company>,
    ApiGetById<Company>,
    ApiSetStatus,
    ApiUpdate<Company>,
    ApiDelete<Company>,
    ApiStatusTransitions,
    ApiGetExport,
    ApiGetCompanyOwnExport
{
  override endpoint = 'companies';

  get(params?: HttpParams, context?: HttpContext): Observable<Company[]> {
    return this.httpClient.get<Company[]>(this.url(), {
      params: params,
      context: context ? context : withStatusMessage(true, false),
    });
  }

  getById(
    id: number,
    params?: HttpParams,
    context?: HttpContext
  ): Observable<Company> {
    return this.httpClient.get<Company>(`${this.url()}/${id}`, {
      params: params,
      context: context ? context : withStatusMessage(true, false),
    });
  }

  getOwn(params?: HttpParams, context?: HttpContext): Observable<Company[]> {
    return this.httpClient.get<Company[]>(`${this.url()}/own`, {
      params: params,
      context: context ? context : withStatusMessage(true, false),
    });
  }

  getOwnById(
    id: number,
    params?: HttpParams,
    context?: HttpContext
  ): Observable<Company> {
    return this.httpClient
      .get<Company[]>(`${this.url()}/own`, {
        params: params,
        context: context ? context : withStatusMessage(true, false),
      })
      .pipe(
        map(companies => {
          return companies.find(c => c.id === id) as Company;
        })
      );
  }

  createOrUpdateOwn(body: Company, context?: HttpContext): Observable<Company> {
    if (body.id) {
      return this.httpClient.put<Company>(`${this.url()}/own`, body, {
        context: context ? context : withStatusMessage(),
      });
    } else {
      return this.httpClient.post<Company>(`${this.url()}/own`, body, {
        context: context ? context : withStatusMessage(),
      });
    }
  }

  update(body: Company, context?: HttpContext): Observable<Company> {
    return this.httpClient.put<Company>(`${this.url()}/${body.id}`, body, {
      context: context ? context : withStatusMessage(),
    });
  }

  delete(id: number, context?: HttpContext): Observable<Company> {
    return this.httpClient.delete<Company>(`${this.url()}/${id}`, {
      context: context ? context : withStatusMessage(),
    });
  }

  setStatus(
    id: number,
    status: string,
    body?: unknown,
    context?: HttpContext
  ): Observable<Company> {
    return this.httpClient.post<Company>(
      `${this.url()}/${id}/${status}`,
      body,
      {
        context: context ? context : withStatusMessage(),
      }
    );
  }

  getAllStatusTransitions(id: number): Observable<StatusTransition[]> {
    return this.httpClient.get<StatusTransition[]>(
      `${this.url()}/${id}/status-transitions`
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getOwnStatusTransitions(id: number): Observable<StatusTransition[]> {
    return this.httpClient.get<StatusTransition[]>(
      `${this.url()}/own/status-transitions`
    );
  }

  getExport(params?: HttpParams, context?: HttpContext): Observable<Blob> {
    return this.httpClient.get(`${this.url()}/csv`, {
      responseType: 'blob',
      params: params,
      context: context ? context : withStatusMessage(true, false),
    });
  }

  getCompanyOwnExport(
    params?: HttpParams,
    context?: HttpContext
  ): Observable<Blob> {
    return this.httpClient.get(`${this.urlCompanyOwn()}/csv`, {
      responseType: 'blob',
      params: params,
      context: context ? context : withStatusMessage(true, false),
    });
  }
}
